import { DocumentTextIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Case as CaseType } from "../../db/schema";
import { extractCaseTitle, stripHtmlTags } from "../../utilities/caseContent";
import Button from "../common/Button";
import ListPage, { ListColumn } from "../common/ListPage";

// A list of the officer's cases to choose ONE from, on the same ListPage and the
// same selection (checkbox, one at a time, action bar) the Files list uses. A
// case is ticked to fill from straight away, or clicked to read it first.
export interface CasesPickerOptions {
  confirmLabel: string;
  onConfirm: (c: CaseListItem) => void;
  onOpenCase: (c: CaseListItem) => void;
}

export type CaseListItem = CaseType & { userName?: string };

const PAGE_SIZE = 25;

const plain = (html: string) => extractCaseTitle(html).replace(/<[^>]*>/g, "").trim() || "Untitled";
const dateOf = (v: string | Date | null | undefined) => (v ? new Date(v).toLocaleDateString() : "");

const CasesList: React.FC<{ picker: CasesPickerOptions }> = ({ picker }) => {
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  // One case at most.
  const [selected, setSelected] = useState<CaseListItem | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(0), [debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/cases", {
        params: { status: "active", search: debounced || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      setCases(res.data.cases || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error loading cases:", error);
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [debounced, page]);
  useEffect(() => {
    void load();
  }, [load]);

  const columns: ListColumn<CaseListItem, "title" | "updated">[] = [
    {
      key: "title",
      header: "Case",
      render: (c) => {
        const html = String((c.content as any)?.data || "");
        return (
          <div className="flex items-center gap-2 min-w-0">
            <DocumentTextIcon className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-medium truncate m-0">{plain(html)}</p>
              <p className="text-xs text-base-content/50 truncate max-w-xl m-0">{stripHtmlTags(html).trim() || "No written narrative"}</p>
            </div>
          </div>
        );
      },
    },
    { key: "updated", header: "Updated", className: "text-sm text-base-content/60 whitespace-nowrap", render: (c) => dateOf(c.updatedAt) },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <ListPage<CaseListItem, "title" | "updated">
      title="Choose a case"
      subtitle="Tick one case to fill the form from its narrative and recordings, or click a case to read it first."
      items={cases}
      getKey={(c) => c.id}
      columns={columns}
      onRowClick={picker.onOpenCase}
      search={{ value: search, onChange: setSearch, placeholder: "Search cases..." }}
      selection={{
        selected: new Set(selected ? [selected.id] : []),
        onToggle: (c) => setSelected((cur) => (cur?.id === c.id ? null : c)),
        noun: "case",
        note: selected ? "One case at a time" : undefined,
        disabledReason: (c) => (selected && selected.id !== c.id ? "One case at a time" : undefined),
        actions: (
          <>
            <Button variant="primary" size="sm" onClick={() => selected && picker.onConfirm(selected)}>
              {picker.confirmLabel}
            </Button>
            <Button variant="clean" size="sm" onClick={() => setSelected(null)}>
              Clear
            </Button>
          </>
        ),
      }}
      loading={loading && cases.length === 0}
      isFiltered={Boolean(debounced)}
      noMatch="No cases match your search."
      empty={{ icon: <DocumentTextIcon className="h-12 w-12" />, title: "No cases yet", description: "Reports you write show up here." }}
      footer={
        total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-base-content/70">
            <span>
              Page {page + 1} of {totalPages} · {total} cases
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )
      }
    />
  );
};

export default CasesList;
